import Clarity from '@microsoft/clarity';
import React, { Suspense, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import Alert from '../components/Alert';
import { DesktopAppHeader } from '../components/DesktopAppHeader';
import { MobileAppHeader } from '../components/MobileAppHeader';
import { ModalStack } from '../components/Modal';
import RouteLoadingAnnouncer from '../components/route-loading-announcer';
import RouteSkeleton from '../components/route-skeleton';
import PageRoutes from './page-routes';
import Providers from './providers';
import { initAnalytics } from '@/helpers/analytics';

// Microsoft Clarity
const clarityProjectID = window.ReactAppEnv?.clarity?.projectID;
if (clarityProjectID) {
  Clarity.init(clarityProjectID);
}

// PostHog. No key configured means no-op.
initAnalytics();

function AppHeader() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="main-header">
      {width < 768 ? <MobileAppHeader /> : <DesktopAppHeader />}
    </div>
  );
}

const App = withRouter(({ location }) => {
  const mainContentRef = React.useRef<HTMLDivElement>(null);
  // Public share pages render bare, with no app chrome
  const isShare = location.pathname.startsWith('/share/');

  return (
    <Providers mainContentRef={mainContentRef}>
      {(error) => (
        <>
          <div className="app">
            {isShare ? null : <AppHeader />}
            {/* Always overflow-y: auto (see App.css) -- it's the app's sole
                scroll container, since html/body/#root/.app all hide their
                own overflow. On short/mobile viewports its content routinely
                exceeds its height, so it needs to be keyboard-focusable to
                stay scrollable for keyboard users (axe: scrollable-region-focusable). */}
            <div className="main-content" ref={mainContentRef} tabIndex={0}>
              {/* Outside every route's Suspense boundary, so its status
                  region is already on the page when a skeleton appears. */}
              <RouteLoadingAnnouncer>
                <Suspense fallback={<RouteSkeleton />}>
                  {error ? (
                    <Alert
                      theme={{ errorAlert: 'error' }}
                      message={error.toString()}
                    />
                  ) : null}
                  <PageRoutes />
                </Suspense>
              </RouteLoadingAnnouncer>
            </div>
            <Suspense fallback={null}>
              <ToastContainer
                newestOnTop
                toastClassName="toast"
                progressClassName="toast-progress"
                hideProgressBar
                closeButton={false}
                closeOnClick={true}
                position="top-center"
              />
            </Suspense>
          </div>
          <Suspense fallback={null}>
            <ModalStack />
          </Suspense>
        </>
      )}
    </Providers>
  );
});

export default App;
